import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api-client";
import { evaluationDetailQueryKey } from "@/hooks/queries/useGetEvaluation";
import {
  EVAL_ERROR,
  type EvaluationDraftBody,
  type SaveDraftResponse,
  type VersionConflictServerState,
} from "@/hooks/queries/evaluation-draft-types";
import { deepJsonEqual } from "@/lib/deep-json-equal";

// Save-evaluation-draft mutation (Story 6.2 endpoint). SHARED SEAM — Story 6.4
// introduces it; 6.3 (diagnosis), 6.5 (save-on-blur scoring) and 6.7 (resume) reuse it.
// `skipAuthRefresh` is OMITTED: post-session data call on the silent-refresh path.
//
// This is the codebase's FIRST custom request-header usage: `X-Client-Version` rides via
// `apiFetch`'s existing `headers` option (api-client.ts is NOT modified). The body is the
// WHOLE `EvaluationDraftBody` (the full-replace contract — see evaluation-draft-types):
// the caller composes it from the full stepper draft state, never a `{tasks}`-only delta.

export interface SaveEvaluationDraftVars {
  /** The evaluation to save into. */
  evaluationId: string;
  /** The version the client believes is current → sent as `X-Client-Version`. */
  version: number;
  /** The ENTIRE current draft (full-replace; omitted fields are cleared server-side). */
  draft: EvaluationDraftBody;
}

export function useSaveEvaluationDraft() {
  const queryClient = useQueryClient();
  return useMutation<SaveDraftResponse, ApiError, SaveEvaluationDraftVars>({
    mutationFn: ({ evaluationId, version, draft }) =>
      apiFetch<SaveDraftResponse>(`/evaluations/${evaluationId}/save-draft`, {
        method: "POST",
        body: draft,
        headers: { "X-Client-Version": String(version) },
      }),
    // The resume read (`["evaluation", id]`) caches the PRE-save snapshot; nothing else
    // invalidates it, so a same-tab Exit→Continue resumes from stale data (Franklin#449).
    // With the global `staleTime: 30_000` a re-mount within 30s would serve that snapshot
    // as fresh WITHOUT a background refetch — so invalidating here (which overrides
    // staleTime and refetches the active query) is what reconciles the cache to the
    // just-saved server state in time for the next resume. The store's own version
    // re-anchor (`setVersion`) keeps the in-memory edit session correct meanwhile.
    onSuccess: (_data, { evaluationId }) => {
      void queryClient.invalidateQueries({
        queryKey: evaluationDetailQueryKey(evaluationId),
      });
    },
  });
}

// --- Typed error helpers (F2.2) ----------------------------------------------
// `apiFetch` already throws a typed `ApiError` carrying `.code` + `.details`, so the
// caller switches on `EVAL_ERROR.*` for corrective UX. These helpers narrow the two
// cases a caller acts on structurally rather than just messaging.

/** Runtime shape guard: `error.details` is `unknown`, so validate before handing it to 6.7. */
function isVersionConflictServerState(
  value: unknown,
): value is VersionConflictServerState {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.version === "number" &&
    typeof candidate.state === "string" &&
    typeof candidate.draft_data === "object" &&
    candidate.draft_data !== null
  );
}

/**
 * Extract the full server draft from a 409 EVAL-002 so the caller can reconcile its
 * local snapshot (AC 8). Returns null for any other error. Until Story 6.7's resume-read
 * lands, this `server_state` IS the reconciliation source — so a malformed envelope must
 * yield null (no reconcile), never a half-built object the reconciler would trust.
 */
export function versionConflictServerState(
  error: unknown,
): VersionConflictServerState | null {
  if (!(error instanceof ApiError) || error.code !== EVAL_ERROR.VERSION_CONFLICT) {
    return null;
  }
  const details = error.details;
  if (details === null || typeof details !== "object") return null;
  const serverState = (details as Record<string, unknown>).server_state;
  return isVersionConflictServerState(serverState) ? serverState : null;
}

/** True when the failure is a stale-version conflict (EVAL-002) the caller should reconcile. */
export function isVersionConflict(error: unknown): boolean {
  return error instanceof ApiError && error.code === EVAL_ERROR.VERSION_CONFLICT;
}

/**
 * The CLINICAL fields of a draft body — everything that is the clinician's actual work.
 * `current_step` is deliberately excluded: it is FE-owned UI navigation state (which step
 * the stepper last showed), not clinical data, and the backend stores it opaquely. Two
 * drafts that agree on every clinical field but disagree on `current_step` carry identical
 * clinical work.
 *
 * This is a BLOCKLIST (drop `current_step`, keep everything else), NOT an allowlist of
 * named fields (gh-470c review — womanonrails #1). An allowlist would silently omit any
 * future *optional* clinical field the wire body gains, so a genuine divergence in that
 * field would be misclassified as a self-reconcile and the clinician would get no notice
 * their on-screen data was replaced — the very safety property this compare protects. The
 * spread keeps every present key; both call sites pass a key-complete body (the FE builds it
 * via `toEvaluationDraftBody`, the server echoes `EvaluationDraft.model_dump()`), so no
 * per-field null defaults are needed. If a future UI-only key is ever added to the wire body,
 * exclude it here alongside `current_step`.
 */
function clinicalFields({
  current_step: _currentStep,
  ...clinical
}: EvaluationDraftBody): Omit<EvaluationDraftBody, "current_step"> {
  return clinical;
}

/**
 * True when a 409 EVAL-002 is the clinician's OWN queued offline edit replaying — a
 * self-reconcile, NOT a cross-device conflict (Franklin#470). On reconnect the offline
 * `update` op drains and advances the SERVER version, but the in-memory `store.version`
 * (hydrated once at mount) stays stale, so the next interactive save 409s even though the
 * server already holds our data. We detect that by comparing the echoed
 * `server_state.draft_data` against the body we just submitted over the CLINICAL fields
 * only: if the clinical work is equal, nothing diverged and there is nothing to re-enter —
 * the caller re-anchors the version silently and proceeds.
 *
 * `current_step` is EXCLUDED from the compare (Franklin#470 follow-up): scoring offline on
 * Scoring, navigating Back to Tasks, then reconnecting and tapping Continue leaves the
 * server at `current_step: "scoring"` (the drained op) while the submitted body carries
 * "tasks" — a difference in the UI nav marker alone. That is still a self-reconcile; only a
 * divergence in the clinician's actual work is a genuine conflict. Comparison is
 * JSON-normalized + order-insensitive (mirrors drain-core's phantom guard, AC12) so a
 * Postgres-JSONB round-trip is never read as a difference.
 */
export function isSelfReconcileConflict(
  submittedBody: EvaluationDraftBody,
  serverState: VersionConflictServerState,
): boolean {
  return deepJsonEqual(
    clinicalFields(submittedBody),
    clinicalFields(serverState.draft_data),
  );
}

/**
 * True when the save was rejected because the draft selects the same task twice (EVAL-007).
 * Keyed on the coded envelope (not HTTP status), symmetric with {@link isVersionConflict}: the
 * duplicate is a DETERMINISTIC validation failure, so the caller surfaces the precise reason
 * (which task is doubled) instead of the generic "please try again" — retrying an unchanged
 * selection fails identically (Franklin#451).
 */
export function isDuplicateTaskError(error: unknown): boolean {
  return error instanceof ApiError && error.code === EVAL_ERROR.DUPLICATE_TASK;
}

/**
 * The offending task code(s) carried by a 422 EVAL-007 envelope
 * (`details.duplicate_task_codes`, set by `EvaluationDuplicateTaskException`). `error.details`
 * is `unknown`, so shape-guard before use: a malformed/absent payload yields `[]` so the caller
 * still shows a corrective duplicate message (never the generic copy, never a throw). Returns
 * `[]` for any non-EVAL-007 error.
 */
export function duplicateTaskCodesFromError(error: unknown): string[] {
  if (!isDuplicateTaskError(error)) return [];
  const details = (error as ApiError).details;
  if (details === null || typeof details !== "object") return [];
  const codes = (details as Record<string, unknown>).duplicate_task_codes;
  if (!Array.isArray(codes)) return [];
  return codes.filter((code): code is string => typeof code === "string");
}
